"use client";
import React, { useMemo, useRef, useState } from "react";
import Button from "@/ui/Button";
import { DeveloperPhoneRole, IHiddenPhones } from "@/types/hiddenPhones.types";
import { propertyService } from "@/services";
import HiddenContactCard from "@/ui/HiddenFieldsView/HiddenContactCard";
import { FormSelect, SelectOption } from "@/ui/Forms";
import AddContactsHolder from "@/ui/HiddenFieldsView/holders/AddContactsHolder";
import ErrorContactsHolder from "@/ui/HiddenFieldsView/holders/ErrorContactsHolder";
import EmptyContactsHolder from "@/ui/HiddenFieldsView/holders/EmptyContactsHolder";
import cn from "clsx";
import { useTranslations } from "next-intl";
import { HiddenContactsEnumEndpoint } from "@/types/enums";

interface Props {
  propertyId: string;
  className?: string;
  isOwner?: boolean;
}

const roleOrderMap: Record<DeveloperPhoneRole, number> = {
  [DeveloperPhoneRole.OWNER]: 0,
  [DeveloperPhoneRole.AGENT]: 1,
  [DeveloperPhoneRole.ASSISTANT]: 2,
  [DeveloperPhoneRole.OTHER]: 3,
};

const HiddenContactView: React.FC<Props> = (props) => {
  const t = useTranslations("HiddenFields");
  const [data, setData] = useState<IHiddenPhones[] | null>(null);
  const [selectValue, setSelectValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const firstFetch = useRef<boolean>(false);

  const options = useMemo<SelectOption[]>(() => {
    const uniqueRoles = Array.from(new Set(data?.map((item) => item.role)));

    return [
      { label: t("all"), value: "" },
      ...uniqueRoles.map((role) => ({
        value: role,
        label: t(role),
      })),
    ];
  }, [data]);

  const loadData = async (id: string) => {
    try {
      setIsLoading(true);
      const contacts = await propertyService.getPropertyHiddenFields(
        id,
        props.isOwner
          ? HiddenContactsEnumEndpoint.owner
          : HiddenContactsEnumEndpoint.developer,
      );
      if (contacts) {
        setData(contacts);
      }
    } catch (err) {
      console.error("CONTACT ERROR", err);
    } finally {
      setIsLoading(false);
      firstFetch.current = true;
    }
  };
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center",
        "pt-[32px] lg:pt-[40px]",
        props.className,
      )}
    >
      {props.isOwner ? (
        <h3 className="text-[#323232] text-[30px] text-centr font-medium mb-[32px] lg:mb-[40px]">
          {t("contacts")}
        </h3>
      ) : (
        <h3 className="text-[#323232] text-[30px] text-centr font-medium mb-[32px] lg:mb-[40px]">
          {t("developerContacts")}
        </h3>
      )}

      {!data && !firstFetch.current && (
        <AddContactsHolder
          isOwner={props.isOwner}
          onAddContacts={() => loadData(props.propertyId)}
          isLoading={isLoading}
        />
      )}
      {!data && firstFetch.current && (
        <ErrorContactsHolder
          onAddContacts={() => loadData(props.propertyId)}
          isLoading={isLoading}
        />
      )}
      {data?.length === 0 && <EmptyContactsHolder isOwner={props.isOwner} />}
      {data && data.length > 0 && !props.isOwner && (
        <div className="w-full md:max-w-[400px]">
          <FormSelect
            value={selectValue}
            options={options}
            onChange={(value) => {
              setSelectValue(value);
            }}
          />
        </div>
      )}
      <div
        className={cn(
          "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full",
          !props.isOwner && "mt-[30px] lg:mt-[50px]",
        )}
      >
        {data
          ?.filter((c) => !selectValue || c.role === selectValue)
          .sort((a, b) => roleOrderMap[a.role] - roleOrderMap[b.role])
          .map((item) => (
            <HiddenContactCard
              isOwner={props.isOwner}
              key={item.id}
              data={item}
              isLinked={true}
            />
          ))}
      </div>
    </div>
  );
};

export default HiddenContactView;
